/** Razorpay Checkout loader — the ONE place the gateway script and the
 * `window.Razorpay` global are touched. The Billing page calls
 * `openRazorpayCheckout` and awaits either the success payload (which
 * the backend then VERIFIES server-side — nothing activates on the
 * browser's word alone) or a rejection when the shopper closes the
 * modal.
 */

export interface RazorpaySuccess {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface RazorpayCheckoutOptions {
  keyId: string;
  amountMinor: number;
  currency: string;
  razorpayOrderId: string;
  name: string;
  description: string;
}

type RazorpayCtor = new (options: Record<string, unknown>) => { open(): void };

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

let scriptPromise: Promise<boolean> | null = null;

export function loadRazorpayScript(): Promise<boolean> {
  const w = window as unknown as { Razorpay?: RazorpayCtor };
  if (w.Razorpay) return Promise.resolve(true);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.onload = () => resolve(true);
      script.onerror = () => {
        scriptPromise = null; // allow a retry on the next attempt
        resolve(false);
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export class CheckoutDismissed extends Error {
  constructor() {
    super("checkout dismissed");
  }
}

export async function openRazorpayCheckout(
  options: RazorpayCheckoutOptions,
): Promise<RazorpaySuccess> {
  const loaded = await loadRazorpayScript();
  const w = window as unknown as { Razorpay?: RazorpayCtor };
  if (!loaded || !w.Razorpay) {
    throw new Error("Payment window could not be loaded — please try again.");
  }
  return new Promise<RazorpaySuccess>((resolve, reject) => {
    const instance = new w.Razorpay!({
      key: options.keyId,
      amount: options.amountMinor,
      currency: options.currency,
      order_id: options.razorpayOrderId,
      name: options.name,
      description: options.description,
      handler: (response: RazorpaySuccess) => resolve(response),
      modal: { ondismiss: () => reject(new CheckoutDismissed()) },
    });
    instance.open();
  });
}
